/**
 * Canonical promotion journey contract: one vocabulary shared by
 * `evt:promotion_presented`, `act:promotion_action`, `evt:promotion_claim_result`,
 * and `evt:promotion_issued` so every mechanic uses the same funnel model.
 *
 * `evt:promotion_issued` is distinct from the pre-existing legacy `evt:promo_issued`
 * (still fired separately by `trackPromoIssued` for the cashback accrual tracker).
 *
 * `accountId` and `sessionId` are deliberately absent — they already ship as
 * PostHog person/session properties, so repeating them here would just add
 * payload weight. `memberId` is the exception: the spec calls it out as a
 * required inline field (not just a person-property join), so
 * `dispatchAnalyticsEvent` enriches these four events with it centrally —
 * see `MEMBER_ID_ENRICHED_EVENTS` in `../dispatch.ts`.
 */

import { matchesClaimGuardCode } from "@utils/promotion-claim-guard";
import { APIErrorCodes, WalletTxMethod } from "kz-ui-sdk";
import { PromotionType } from "./types";

/** Where the promotion was rendered. `surfacePage` carries which route it was on. */
export enum PromotionSurface {
  HOMEPAGE = "homepage",
  PROMOTION_PAGE = "promotion_page",
  POPUP = "popup",
  DRAWER = "drawer",
  FAB = "fab",
  IN_GAME_PROMPT = "in_game_prompt",
  EXTERNAL_LINK = "external_link",
  /** popup auto-displayed after the last ball was used. */
  AUTO_DISPLAY = "auto_display",
  /** user found the referral CTA themselves (top-right Share icon, drawer). */
  MANUAL_NAV = "manual_nav",
}

export enum PromotionActionType {
  OPEN = "open",
  CLAIM = "claim",
  SHARE = "share",
  COPY = "copy",
  REDEEM = "redeem",
  PLAY = "play",
  GET_MORE = "get_more",
  DISMISS = "dismiss",
  /** Tapped "Learn More". Not wired yet — the S1 nudge modal it lives on doesn't exist in MP. */
  LEARN_MORE = "learn_more",
}

export enum PromotionResultStatus {
  SUCCESS = "success",
  REJECTED = "rejected",
  FAILED = "failed",
  DUPLICATE = "duplicate",
  DEFERRED = "deferred",
}

/** Controlled vocabulary only — never send free text or raw backend messages. */
export enum PromotionReasonCode {
  BELOW_THRESHOLD = "below_threshold",
  NOT_ELIGIBLE = "not_eligible",
  BLACKLISTED = "blacklisted",
  EXPIRED = "expired",
  ALREADY_CLAIMED = "already_claimed",
  CLAIMED_ON_OTHER_DEVICE = "claimed_on_other_device",
  PROMOTION_DISABLED = "promotion_disabled",
  MODAL_BLOCKED = "modal_blocked",
  IN_GAME_DEFERRED = "in_game_deferred",
  NETWORK_ERROR = "network_error",
  SERVER_ERROR = "server_error",
  INVALID_CODE = "invalid_code",
  CODE_EXHAUSTED = "code_exhausted",
  SELF_REDEEM = "self_redeem",
  UNKNOWN = "unknown",
}

export enum PromotionClaimMode {
  MANUAL = "manual",
  AUTO = "auto",
  SYSTEM = "system",
}

/**
 * Splits "credited a reward" from "resolved with no reward" — Free Spin blanks
 * would otherwise be indistinguishable from `amount: 0` meaning "unknown".
 */
export enum PromotionRewardOutcome {
  REWARD = "reward",
  BLANK = "blank",
}

export interface PromotionEventBase {
  promotionType: PromotionType;
  /** Only when an API supplies one. Omitted rather than faked. */
  promotionId?: string;
  surface: PromotionSurface;
  /** Route the surface appeared on, e.g. "/prizes". */
  surfacePage: string;
  claimId?: string;
  cycleId?: string;
  /** Share & Earn only — the specific reward-split record between sharer and friend. */
  allocationId?: string;
  /** Share & Earn only — same value as `allocationId` today: no separate relationship
   * record exists apart from the allocation, but the spec names it as its own field. */
  relationshipId?: string;
  /** Share & Earn only — the invited friend's member id, from either party's tx record. */
  friendMemberId?: string;
  /**
   * Free Code only — an opaque id for the specific generated code record. Never the
   * redeemable code text itself (never send that to PostHog).
   */
  codeId?: string;
  /** Free Code only — the redeemer's member id. Only available from the redeemer's
   * own tx record; the giver's record has no per-redemption identity to report. */
  redeemerMemberId?: string;
}

export interface PromotionPresentedMetadata extends PromotionEventBase {
  isEligible?: boolean;
  isClaimable?: boolean;
  isLocked?: boolean;
}

export interface PromotionActionMetadata extends Omit<PromotionEventBase, "promotionType"> {
  /**
   * `string` widens this beyond the base's `PromotionType` enum only here: TK-815's
   * live PostHog exposure filter depends on the literal label `"Refer & Earn"`, not
   * the enum value. New callers should still pass a real `PromotionType`.
   */
  promotionType: PromotionType | string;
  action: PromotionActionType;
  claimMode?: PromotionClaimMode;
  channel?: string;
  shareConfirmed?: boolean;
  /** TK-815's referral code, when the action is on a referral CTA. */
  referralCode?: string;
  /** TK-815's exposure variant, when the action fired inside that experiment. */
  experimentVariant?: string;
  /**
   * Finer-grained than `surfacePage` — distinguishes sub-screens under the same
   * route (e.g. Refer & Earn's Overview tab vs. Records tab). TK-815's `screenContext`.
   */
  screenContext?: string;
}

export interface PromotionClaimResultMetadata extends PromotionEventBase {
  claimId: string;
  resultStatus: PromotionResultStatus;
  reasonCode?: PromotionReasonCode;
  claimMode: PromotionClaimMode;
  rewardAmount?: number;
  rewardOutcome?: PromotionRewardOutcome;
}

export interface PromotionIssuedMetadata extends Omit<PromotionEventBase, "surface"> {
  /** Optional here (unlike the base): can fire from a global, page-agnostic listener with no single surface. */
  surface?: PromotionSurface;
  amount: number;
  rewardOutcome: PromotionRewardOutcome;
  claimMode: PromotionClaimMode;
  /** Legacy taxonomy value, kept for consistency with `evt:promo_issued` and easier cross-referencing. */
  promoType: string;
}

/** FE-minted join key threaded action -> result -> issued. Links frontend events only; won't join to a backend reward ledger. */
export function createPromotionClaimId(): string {
  return `mp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Only consumed today by the cashback issuance trackers (`CashbackIssuedTracker`,
 * `RewardBox`). The P2-mechanic entries below have no caller yet — those flows
 * pass their `PromotionType` explicitly rather than deriving it here.
 */
const WALLET_TX_METHOD_TO_PROMOTION_TYPE: Record<string, PromotionType> = {
  [WalletTxMethod.InstantCashBack]: PromotionType.CASHBACK,
  [WalletTxMethod.DailyCashBack]: PromotionType.CASHBACK,
  [WalletTxMethod.WeeklyCashBack]: PromotionType.CASHBACK,
  [WalletTxMethod.MonthlyCashBack]: PromotionType.CASHBACK,
  [WalletTxMethod.CashbackMission]: PromotionType.CASHBACK_MISSION,
  [WalletTxMethod.DailyBonus]: PromotionType.DAILY_BONUS,
  [WalletTxMethod.PlaySpin]: PromotionType.PLAY_AND_SPIN,
  [WalletTxMethod.SkinnerBox]: PromotionType.SKINNER_BOX,
  [WalletTxMethod.Compensation]: PromotionType.CS_COMPENSATION,
  [WalletTxMethod.ReferralCommission]: PromotionType.REFERRAL,
  [WalletTxMethod.SecretCodeSharer]: PromotionType.SECRET_CODE_SHARER,
  [WalletTxMethod.SecretCodeSharee]: PromotionType.SECRET_CODE_REDEEMER,
  [WalletTxMethod.MegaShare]: PromotionType.MEGA_SHARE,
  [WalletTxMethod.MegaSharer]: PromotionType.MEGA_SHARE,
  [WalletTxMethod.MegaSharee]: PromotionType.MEGA_SHARE,
};

export function promotionTypeFromWalletTxMethod(method: string): PromotionType | undefined {
  return WALLET_TX_METHOD_TO_PROMOTION_TYPE[method];
}

type FailureRule = {
  codes: readonly string[];
  resultStatus: PromotionResultStatus;
  reasonCode: PromotionReasonCode;
};

/**
 * Ordered most-specific first. `matchesClaimGuardCode` walks the whole
 * RTK Query error object, so this works on both `{ error }` results and bare
 * error payloads.
 */
const FAILURE_RULES: readonly FailureRule[] = [
  {
    codes: [
      APIErrorCodes.ClaimReward.AlreadyClaimed,
      APIErrorCodes.Compensation.AlreadyClaimed,
      APIErrorCodes.Compensation.NotFound,
      APIErrorCodes.PlaySpin.AlreadyExists,
    ],
    resultStatus: PromotionResultStatus.DUPLICATE,
    reasonCode: PromotionReasonCode.ALREADY_CLAIMED,
  },
  {
    codes: [APIErrorCodes.ClaimReward.MemberBlacklisted],
    resultStatus: PromotionResultStatus.REJECTED,
    reasonCode: PromotionReasonCode.BLACKLISTED,
  },
  {
    codes: [APIErrorCodes.ClaimReward.InvalidClaimPeriod],
    resultStatus: PromotionResultStatus.REJECTED,
    reasonCode: PromotionReasonCode.EXPIRED,
  },
  {
    codes: [
      APIErrorCodes.ClaimReward.NotConfigured,
      APIErrorCodes.PlaySpin.NotEnabled,
      APIErrorCodes.PlaySpin.SettingsNotFound,
    ],
    resultStatus: PromotionResultStatus.REJECTED,
    reasonCode: PromotionReasonCode.PROMOTION_DISABLED,
  },
  {
    codes: [
      APIErrorCodes.PlaySpin.NotEligible,
      APIErrorCodes.PlaySpin.InvalidMemberGroup,
      APIErrorCodes.PlaySpin.NotClaimable,
      APIErrorCodes.ClaimReward.UnableRedeem,
      APIErrorCodes.ClaimReward.SkinnerBoxNotClaimable,
    ],
    resultStatus: PromotionResultStatus.REJECTED,
    reasonCode: PromotionReasonCode.NOT_ELIGIBLE,
  },
  {
    codes: [
      APIErrorCodes.SecretCode.CodeRequired,
      APIErrorCodes.SecretCode.ParticipantNotFound,
      APIErrorCodes.SecretCode.NotSameBrand,
    ],
    resultStatus: PromotionResultStatus.REJECTED,
    reasonCode: PromotionReasonCode.INVALID_CODE,
  },
  {
    codes: [APIErrorCodes.SecretCode.CodeAlreadyFullyRedeemed],
    resultStatus: PromotionResultStatus.DUPLICATE,
    reasonCode: PromotionReasonCode.CODE_EXHAUSTED,
  },
  {
    codes: [APIErrorCodes.SecretCode.SelfClaim],
    resultStatus: PromotionResultStatus.REJECTED,
    reasonCode: PromotionReasonCode.SELF_REDEEM,
  },
  {
    codes: [
      APIErrorCodes.SecretCode.CampaignCancelled,
      APIErrorCodes.SecretCode.CampaignDeleted,
      APIErrorCodes.SecretCode.CampaignEnded,
      APIErrorCodes.SecretCode.CampaignExpired,
    ],
    resultStatus: PromotionResultStatus.REJECTED,
    reasonCode: PromotionReasonCode.EXPIRED,
  },
  {
    codes: [APIErrorCodes.MegaShare.NotClaimable, APIErrorCodes.MegaShare.BonusAlreadyReceived],
    resultStatus: PromotionResultStatus.DUPLICATE,
    reasonCode: PromotionReasonCode.ALREADY_CLAIMED,
  },
];

function readErrorStatus(resultOrError: unknown): string | number | undefined {
  if (!resultOrError || typeof resultOrError !== "object") return undefined;
  const withError = resultOrError as { error?: { status?: string | number }; status?: string | number };
  return withError.error?.status ?? withError.status;
}

/**
 * Maps a failed claim response onto the controlled `resultStatus` / `reasonCode`
 * pair. Anything unrecognised lands on `unknown` rather than leaking a raw
 * backend string into PostHog.
 */
export function resolvePromotionClaimFailure(resultOrError: unknown): {
  resultStatus: PromotionResultStatus;
  reasonCode: PromotionReasonCode;
} {
  for (const rule of FAILURE_RULES) {
    if (matchesClaimGuardCode(resultOrError, rule.codes)) {
      return { resultStatus: rule.resultStatus, reasonCode: rule.reasonCode };
    }
  }

  const status = readErrorStatus(resultOrError);
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return { resultStatus: PromotionResultStatus.FAILED, reasonCode: PromotionReasonCode.NETWORK_ERROR };
  }
  if (typeof status === "number" && status >= 500) {
    return { resultStatus: PromotionResultStatus.FAILED, reasonCode: PromotionReasonCode.SERVER_ERROR };
  }

  return { resultStatus: PromotionResultStatus.REJECTED, reasonCode: PromotionReasonCode.UNKNOWN };
}
